/**
 * useUcConfig — экономика UTC (начисления/штрафы клиентского приложения).
 * Только OO (perm actor_approval). Значения хранятся в app_config (ключи uc.*),
 * читаются/пишутся через /api/oo/uc-config; источник правды — бэкенд getUcConfig.
 *
 * Backend:
 *  - GET /api/oo/uc-config            → UcConfig (все 6 значений, number)
 *  - PUT /api/oo/uc-config  body Partial<UcConfig> (0..1000) → { ok, config: UcConfig }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export interface UcConfig {
  game_completion: number;
  achievement: number;
  referral_join: number;
  referral_app: number;
  referral_clawback: number;
  add_friend_to_game: number;
}

export interface UseUcConfigResult {
  config: UcConfig | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  save: (next: Partial<UcConfig>) => Promise<{ ok: boolean; error?: string; config?: UcConfig }>;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Ошибка";
}

export function useUcConfig(): UseUcConfigResult {
  const [config, setConfig] = useState<UcConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<UcConfig>("/api/oo/uc-config", { signal: ac.signal });
        if (ac.signal.aborted) return;
        setConfig(r);
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setError(errMsg(e));
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [trigger]);

  const save = useCallback(async (next: Partial<UcConfig>) => {
    try {
      const r = await api<{ ok: boolean; config: UcConfig }>("/api/oo/uc-config", { method: "PUT", body: next });
      if (r?.config) setConfig(r.config);
      return { ok: true, config: r?.config };
    } catch (e: unknown) {
      return { ok: false, error: errMsg(e) };
    }
  }, []);

  return { config, loading, error, reload, save };
}
